import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    FormControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { getCaptionString } from '@modeso/aumico-lib-core-fe';
import { IFinancialLineItemComponent, IFli, IManualAccount } from '@modeso/types__aumico-lib-annualreport-be';
import { ViewMode } from '@modeso/types__aumico-lib-core-be';
import { IFinancialLineItemOptions } from '@modeso/types__aumico-lib-notes-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { cloneDeep, differenceBy, findIndex } from 'lodash-es';
import { ViewState } from '../../enums/ViewState.enum';
import { FliHelper } from '../../helpers/fli.helper';
import { IAnnualReportDefaultSettings } from '../../interfaces/IAnnualReportDefaultSettings.interface';
import { FxCurrencyDisplay, IFxNodeStamp, IFxNotesContext, effectiveFxCurrencyDisplay } from '../../interfaces/IFxNotesContext.interface';
import { IFLIForNotes } from '../../interfaces/IFLIForNotes.interface';
import { NotesPageBase } from '../NotesPageBase';
import { ITableWithForm } from '../tableWithForm/ITableWithForm.interface';
import { ITableWithFormOptions } from '../tableWithForm/ITableWithFormOptions.interface';
import { IFinancialLineItemDelegate } from './IFliDelegate.component';

const debug = Debug('aumico:aumico-lib-notes-fe:FliComponent');

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'aumico-notes-fli',
    templateUrl: 'fli.component.html',
    styleUrls: ['fli.component.scss'],
})
export class FliComponent extends NotesPageBase implements OnInit, OnChanges {
    DELEGATE?: IFinancialLineItemDelegate;
    private _state: ViewState;

    @Input() public set delegate(delegate: IFinancialLineItemDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }

    @Input() editable = true;

    @Input()
    get state(): ViewState {
        return this._state;
    }
    set state(value: ViewState) {
        this._state = value;
    }

    @Input() mode: ViewMode = ViewMode.PREVIEW;
    @Input()
    get viewMode(): ViewMode {
        return this.mode;
    }
    set viewMode(value: ViewMode) {
        this.mode = value;
    }

    @Input() printIndex: number = 0;
    @Input() componentIndex: number;

    @Input() set resetValuesFromMappingTriggered(value: boolean) {
        if (!value) {
            return;
        }
        this.resetFormValuesFromMapping();
    }

    private defaultSettings: IAnnualReportDefaultSettings;
    /** FX (REV-21512): null on non-FX reports — the table then renders exactly as today (G0). */
    @Input() fxNotesContext: IFxNotesContext | null = null;
    /** FX (REV-21512): per-account display values, keyed `id|accountName`. */
    @Input() fliFxStamps: Map<string, IFxNodeStamp> | null = null;

    /** The 3-state chip's effective state; absent option means `both` on FX reports. */
    get fxCurrencyDisplay(): FxCurrencyDisplay {
        return effectiveFxCurrencyDisplay(this.componentOptions, this.fxNotesContext != null);
    }

    @Input() set annualReportDefaultSettings(value: IAnnualReportDefaultSettings) {
        this.defaultSettings = value;
    }

    public latestFliWhichHasBeenSelected: IFli | undefined;

    form: UntypedFormGroup;
    tableOptions: Map<string, ITableWithFormOptions> = new Map();
    balanceTableProperties: ITableWithFormOptions;
    incomeTableProperties: ITableWithFormOptions;
    displayedColumns: string[] = ['accountName', 'currentValue', 'previousValue'];
    selectedFlis: Map<number, IFli> = new Map();
    eligibleFlis: Map<number, IFli[]> = new Map();
    fliFieldList: UntypedFormArray;
    fliViewMode: Array<ViewMode>;
    changedData: IFinancialLineItemComponent;
    nullSelectionChangeDetection = false;
    viewState = ViewState;
    fliTitleControl = new FormControl();

    readonly propertyOfFliOptionsToDisplay = 'caption';

    private possibleSelectionOptions: IFli[] = [];

    showFliClearanceConfirmationDialog: boolean;
    private fliSectionIndexToClear: number;

    emptyFLIselector: number;
    // Initializing flis with data to be previewed in notes type form
    @Input() flis: IFLIForNotes[] = []; // input for all existing flis
    @Input() language = 'de-ch';
    @Input() defaultLanguage = 'de-ch';
    @Input() fliData = this.fliHelper.getFliComponentMockedData();

    // separate componentOptions input for preview of notes type form
    @Input() componentOptions: IFinancialLineItemOptions = this.fliData.options;

    @Input() highlightTodos = false;

    @Input() tableNumbers: string[] = [];

    @Output() onDropFli: EventEmitter<{ previousIndex: number; currentIndex: number }> = new EventEmitter();

    constructor(
        private fb: UntypedFormBuilder,
        private fliHelper: FliHelper,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private readonly changeDetector: ChangeDetectorRef,
    ) {
        super();
        this.handleFLiFormChange = this.handleFLiFormChange.bind(this);
        this.addSvgIcon('aumico-pattern-icon-remove');
        this.addSvgIcon('aumico-pattern-icon-edit-write');
        this.addSvgIcon('aumico-icon-pattern-drag-component');
    }

    // To update component Options when input for fliOptions is passed
    ngOnChanges(change: SimpleChanges) {
        if (change['fliData'] && change['fliData'].currentValue.options) {
            this.componentOptions = change['fliData'].currentValue.options;
        }
    }

    ngOnInit(): void {
        debug('ngOnInit');
        // REV-20899: Refresh saved FLI values from the live `flis` input
        // before initializing the form. The saved `fliData` snapshot was
        // captured the last time the user opened the notes step — any
        // reclassifications added afterwards (e.g. in grouping & masking)
        // live only on `this.flis`, which `FlisMapper.getFlisOfBalanceAndIncomeTemplates`
        // builds from finStatements with reclasses already applied. Without
        // this refresh the FLI table renders stale per-account values and a
        // stale total.
        this.refreshFliDataFromLiveFlis();
        this.changedData = cloneDeep(this.fliData);
        this.initializeForm();
        this.setTableOptionsVariants();
        this.addPreselectedFlis(); // if FliData already has selected flis saved
        this.monitorFliChange();
        debug(this.form, 'this.form');
    }

    /**
     * REV-20899: replace each saved FLI's per-account values and total row
     * with the corresponding live values (which include the transactional
     * reclassifications). Saved FLI structure / footnotes / numbering are
     * preserved; only the value-bearing fields are refreshed. FLIs that no
     * longer exist live (mapping was removed) are dropped, mirroring the
     * behaviour of `FliHelper.getUpdatedFlis` which is used by the existing
     * manual "reset from mapping" trigger.
     */
    private refreshFliDataFromLiveFlis(): void {
        if (
            this.fliData == null ||
            !Array.isArray(this.fliData.flis) ||
            this.fliData.flis.length === 0 ||
            !Array.isArray(this.flis) ||
            this.flis.length === 0
        ) {
            return;
        }
        this.fliData.flis = [...this.fliHelper.getUpdatedFlis(this.fliData.flis, this.flis)];
    }

    initializeForm() {
        this.form = this.fb.group({
            fliSelectors: this.fb.array([this.createFliSelectorField()]),
        });
        this.fliFieldList = this.form.get('fliSelectors') as UntypedFormArray;

        this.fliViewMode = new Array();

        if (this.componentOptions != null) {
            this.fliTitleControl.setValue(this.componentOptions.text[this.language]);
        }
    }

    addPreselectedFlis() {
        if (this.changedData.flis == null) {
            return;
        }
        let viewMode = this.state === ViewState.EDITING ? ViewMode.EDIT : ViewMode.PREVIEW;
        if (this.changedData.flis.length) {
            debug('addPreselectedFlis');
            let index = 0;
            // removing the initially created select empty field
            this.fliFieldList?.clear();
            this.fliFieldList?.removeAt(0);
            this.changedData.flis.forEach((fli: IFli, sectionIndex: number) => {
                const formGroup = {};
                formGroup['fli'] = new UntypedFormControl(fli, Validators.required);
                this.fliFieldList?.push(this.fb.group(formGroup));
                this.handleSelectionChange(fli, index);
                this.fliViewMode?.push(viewMode);
                index++;
            });
        } else {
            this.fliViewMode?.push(viewMode);
        }
    }

    createFliSelectorField(): UntypedFormGroup {
        const formGroup = {};
        formGroup['fli'] = new UntypedFormControl('', Validators.required);
        return this.fb.group(formGroup);
    }

    // Whenever an fli selection is changed or a new one is added
    monitorFliChange() {
        this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(this.handleFLiFormChange);
        this.fliTitleControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            this.componentOptions.text[this.language] = value;
            this.changedData.options = this.componentOptions;
            this.DELEGATE.fliComponentData(this.changedData, this.componentIndex);
        });
    }

    private handleFLiFormChange(value: { fliSelectors: [{ fli: IFli }] }) {
        const changeOfFlis: IFli[] = this.fliHelper.getFlisFromFormChange(value.fliSelectors);

        const difference = differenceBy(changeOfFlis, this.changedData.flis, 'id')[0];
        // '' value is set when user add new record but without selection
        // and it should be ignored
        if (difference != null && difference.toString() !== '') {
            this.applyAdditionOfFliDifference(changeOfFlis, difference);
        }

        this.DELEGATE.fliComponentData(this.changedData, this.componentIndex);
    }

    private applyAdditionOfFliDifference(changeOfFlis: IFli[], difference: IFli) {
        const isLengthOfFliDataEqualToTheChange: boolean = changeOfFlis.length === this.fliData.flis.length;
        const isLengthOfChangedDataEqualToTheChange: boolean = changeOfFlis.length === this.changedData.flis.length;

        if (isLengthOfFliDataEqualToTheChange && isLengthOfChangedDataEqualToTheChange) {
            // one of the selections is changed to new selection
            const indexHasChangeInValue: number = findIndex(changeOfFlis, difference);
            this.fliData.flis[indexHasChangeInValue] = difference;
            this.changedData.flis[indexHasChangeInValue] = difference;
        } else {
            // insert the newly added selection at the correct index (do NOT push to the end)
            const insertIndex: number = findIndex(changeOfFlis, difference);
            if (insertIndex != null && insertIndex >= 0) {
                this.fliData.flis.splice(insertIndex, 0, difference);
                this.changedData.flis.splice(insertIndex, 0, difference);
                // keep footnotes aligned if they exist
                if (this.changedData.footnotes) {
                    this.changedData.footnotes.splice(insertIndex, 0, '');
                }
            } else {
                // fallback in case index detection fails
                this.fliData.flis.push(difference);
                this.changedData.flis.push(difference);
                if (this.changedData.footnotes) {
                    this.changedData.footnotes.push('');
                }
            }
        }
    }

    remove(index: number) {
        this.fliFieldList = this.form.get('fliSelectors') as UntypedFormArray;

        this.fliFieldList.removeAt(index);
        if (index > -1) {
            this.fliViewMode.splice(index, 1);
        }

        const flis: IFli[] = [];
        this.fliFieldList.value.forEach((field: { fli: IFli }) => {
            if (field.fli != null) {
                flis.push(field.fli);
            }
        });

        // Preserve previous footnotes mapped by FLI id to keep remaining footnotes aligned
        const prevFlis = this.changedData.flis ? [...this.changedData.flis] : [];
        const prevFootnotes = this.changedData.footnotes ? [...this.changedData.footnotes] : [];
        const footnoteById = new Map<string, string>(prevFlis.map((fli, i) => [fli.id, prevFootnotes[i] ?? '']));

        this.fliData.flis = flis;
        this.changedData.flis = this.fliHelper.getDifferenceOfFlisById(this.changedData.flis, flis);

        // Remove UI footnote at the same visual index
        if (this.fliData?.footnotes && this.fliData.footnotes.length > index) {
            this.fliData.footnotes.splice(index, 1);
        }

        // Rebuild changedData.footnotes according to the new FLIs order
        if (this.changedData.flis && this.changedData.flis.length > 0) {
            this.changedData.footnotes = this.changedData.flis.map((f) => footnoteById.get(f.id) ?? '');
        } else {
            this.changedData.footnotes = [];
        }
        const selectedFliData: IFinancialLineItemComponent = cloneDeep(this.changedData);
        selectedFliData.flis = selectedFliData.flis.filter((fli: IFli) => fli != null);
        this.DELEGATE.fliComponentData(selectedFliData, this.componentIndex);

        // This part handles adjusting selection options due to index changes as a result of the remove
        for (let i = index; i < this.selectedFlis.size; i++) {
            this.selectedFlis.set(i, this.selectedFlis.get(i + 1));
            this.eligibleFlis.set(i, this.eligibleFlis.get(i + 1));
        }
    }

    toggleMode(index: number) {
        this.fliViewMode[index] = this.fliViewMode[index] === ViewMode.PREVIEW ? ViewMode.EDIT : ViewMode.PREVIEW;
    }

    addItem(index: number) {

        // 1) If a single empty selector exists, remove it and adjust the target index
        const emptyIdx = this.fliFieldList.controls.findIndex((control) => {
            const v = control.get('fli')?.value;
            return v == null || v === '';
        });

        if (emptyIdx !== -1) {
            // Remove empty form control
            this.fliFieldList.removeAt(emptyIdx);

            // Keep view modes aligned
            if (emptyIdx > -1 && emptyIdx < this.fliViewMode.length) {
                this.fliViewMode.splice(emptyIdx, 1);
            }

            // Shift maps down starting from the removed index
            for (let i = emptyIdx; i < this.selectedFlis.size; i++) {
                this.selectedFlis.set(i, this.selectedFlis.get(i + 1));
                this.eligibleFlis.set(i, this.eligibleFlis.get(i + 1));
            }

            // Adjust target index if the removed empty was before or at the clicked position
            if (emptyIdx <= index) {
                index = index - 1;
            }

            // Also remove placeholder UI footnote for the deleted empty selector
            if (this.fliData?.footnotes && this.fliData.footnotes.length > emptyIdx) {
                this.fliData.footnotes.splice(emptyIdx, 1);
            }
        }

        // 2) Compute insert position (after current index); fallback to append if index is invalid
        const maxIndex = this.fliFieldList.length - 1;
        const insertAt = typeof index === 'number' && index >= 0 && index <= maxIndex ? index + 1 : this.fliFieldList.length;

        this.emptyFLIselector = insertAt

        // 3) Insert new empty selector and keep arrays/maps in sync
        this.fliFieldList.insert(insertAt, this.createFliSelectorField());

        const vm = this.state === ViewState.EDITING ? ViewMode.EDIT : ViewMode.PREVIEW;
        this.fliViewMode.splice(insertAt, 0, vm);

        // Insert placeholder UI footnote for the new empty selector to keep indices aligned in the template
        if (this.fliData?.footnotes) {
            this.fliData.footnotes.splice(insertAt, 0, '');
        }

        // Shift maps up from the end to make room at insertAt
        for (let i = this.selectedFlis.size; i > insertAt; i--) {
            this.selectedFlis.set(i, this.selectedFlis.get(i - 1));
            this.eligibleFlis.set(i, this.eligibleFlis.get(i - 1));
        }
        // Clear mapping at the newly inserted slot
        this.selectedFlis.delete(insertAt);
        this.eligibleFlis.delete(insertAt);

        this.changeDetector.detectChanges();
    }

    private setTableOptionsVariants() {
        this.balanceTableProperties = {
            properties: [
                // id is only added for identification of row but will be hidden in table
                { name: '', type: 'text', property: 'accountName', required: true, flex: 30 },
                { name: '', type: 'text', property: 'id', required: false },
                {
                    name: this.fliData.currentBalanceYearDate,
                    type: 'amount',
                    headerType: 'date',
                    property: 'currentValue',
                    hasTotal: true,
                    required: false,
                    highlighted: true,
                    flex: 10,
                    disabled: true,
                },
                {
                    name: this.fliData.previousBalanceYearDate,
                    type: 'amount',
                    headerType: 'date',
                    property: 'previousValue',
                    hasTotal: true,
                    required: false,
                    flex: 10,
                    disabled: true,
                },
            ],
        };

        this.incomeTableProperties = {
            properties: [
                // id is only added for identification of row but will be hidden in table
                { name: '', type: 'text', property: 'accountName', required: true, flex: 30 },
                { name: '', type: 'text', property: 'id', required: false },
                {
                    name: this.fliData.currentIncomeYearDate,
                    type: 'amount',
                    headerType: 'date',
                    property: 'currentValue',
                    hasTotal: true,
                    required: false,
                    highlighted: true,
                    flex: 10,
                    disabled: true,
                },
                {
                    name: this.fliData.previousIncomeYearDate,
                    type: 'amount',
                    headerType: 'date',
                    property: 'previousValue',
                    hasTotal: true,
                    required: false,
                    flex: 10,
                    disabled: true,
                },
            ],
        };
    }

    // Tracks accounts table changes
    onFormChange(form: ITableWithForm, index: number) {
        // We need to know the fli Id of the fli index that has been changed in order to apply these changes in the correct fli.
        const fliId: string = this.form.value.fliSelectors[index].fli.id;

        const updateOfFliData: IFinancialLineItemComponent = this.fliHelper.updateWithFormChange(
            fliId,
            this.changedData,
            form,
        );
        this.changedData = cloneDeep(updateOfFliData);

        // Handle footnotes - map by FLI id to avoid index drift due to empty selectors
        const changedIdx = this.changedData.flis.findIndex((f) => f.id === fliId);
        const targetIndex = changedIdx !== -1 ? changedIdx : index;
        if (form.footnote && form.footnote.trim() !== '') {
            if (!this.changedData.footnotes) {
                this.changedData.footnotes = [];
            }
            this.changedData.footnotes[targetIndex] = form.footnote;
        } else if (this.changedData.footnotes) {
            this.changedData.footnotes[targetIndex] = '';
        }

        if (this.DELEGATE) {
            const fliData: IFinancialLineItemComponent = cloneDeep(this.changedData);
            this.DELEGATE.fliComponentData(fliData, this.componentIndex);
        }
    }

    // To decide whether to show accounts table or not
    isFliSelected(index: number) {
        if (this.form.value.fliSelectors[index].fli) {
            return true;
        }
        return false;
    }

    getMatchingAccounts(index: number): IManualAccount[] {
        let accounts: IManualAccount[] = [];
        const selectedFli: IFli = this.form.value.fliSelectors[index].fli;

        if (selectedFli) {
            accounts = selectedFli.accounts;
        }
        return accounts;
    }

    getTotalRowTextOfFli(index: number): string {
        let totalRowText: string;
        const selectedFli: IFli = this.form.value.fliSelectors[index].fli;

        if (selectedFli) {
            totalRowText = selectedFli.totalRow != null ? selectedFli.totalRow.accountName : undefined;
        }
        return totalRowText;
    }

    onSelectionChanged(selectedFli: IFli, fliSectionIndex: number) {
        // The secion index
        if (selectedFli != null) {
            this.emptyFLIselector = undefined;
            if (this.changedData.flis[fliSectionIndex] !== undefined) {
                this.changedData.flis[fliSectionIndex].addNumbering = true;
            }
            this.handleSelectionChange(selectedFli, fliSectionIndex);
        }
    }

    getOptions(id: string): ITableWithFormOptions {
        const result = this.tableOptions.get(id);
        return result;
    }

    /**
     * REV-21347: resolve a possibly-object FLI caption (pre-migration `{ 'de-ch': … }` shape) to a
     * display string in the report language, so the FLI title never renders "[object Object]".
     */
    resolveCaption(caption: IFli['caption']): string {
        return getCaptionString(caption, this.language, this.defaultLanguage);
    }

    getCurrentDateForFli(fli: IFli | null): string | undefined {
        if (!fli || !this.defaultSettings) return undefined;
        return fli.belongingTemplateType === TemplateType.INCOME
            ? this.defaultSettings.currentIncomeDate
            : this.defaultSettings.currentBalanceDate;
    }

    getPreviousDateForFli(fli: IFli | null): string | undefined {
        if (!fli || !this.defaultSettings) return undefined;
        return fli.belongingTemplateType === TemplateType.INCOME
            ? this.defaultSettings.previousIncomeDate
            : this.defaultSettings.previousBalanceDate;
    }

    private handleSelectionChange(selectedFli: IFli, fliSectionIndex: number) {
        // re-adjusting table properties according to type of selected fli
        if (selectedFli.belongingTemplateType === TemplateType.INCOME) {
            this.tableOptions.set(selectedFli.id, this.incomeTableProperties);
        } else {
            this.tableOptions.set(selectedFli.id, this.balanceTableProperties);
        }

        this.latestFliWhichHasBeenSelected = cloneDeep(selectedFli);
        this.form
            .get('fliSelectors')
        ['controls'][fliSectionIndex]['controls']['fli'].setValue(this.latestFliWhichHasBeenSelected);

        const isOldSelectionChangedByNewOne: boolean =
            this.selectedFlis.has(fliSectionIndex) && this.selectedFlis.get(fliSectionIndex) != null;

        if (!isOldSelectionChangedByNewOne) {
            this.selectedFlis.set(fliSectionIndex, selectedFli);
        } else {
            const oldFli: IFli = this.selectedFlis.get(fliSectionIndex);
            this.addToEligibleFlis(oldFli);
            this.selectedFlis.set(fliSectionIndex, selectedFli);
        }
    }

    public openShowFliClearanceConfirmationDialog(index: number): void {
        const selectedFli = this.selectedFlis.get(index);
        if (selectedFli != null) {
            // need to reselect fli again because hamSimpleDropDown already emptied internally the selection
            this.handleSelectionChange(this.selectedFlis.get(index), index);
        }

        this.showFliClearanceConfirmationDialog = true;
        this.fliSectionIndexToClear = index;
    }

    clearFliSelection(): void {
        this.latestFliWhichHasBeenSelected = null;
        // Remove the FLI from the form array instead of just setting it to null
        this.remove(this.fliSectionIndexToClear);

        // If the form is now empty, make sure to update data accordingly
        if (this.fliFieldList.length === 0) {
            const emptyData = cloneDeep(this.changedData);
            emptyData.flis = [];
            this.DELEGATE?.fliComponentData(emptyData, this.componentIndex);
        }

        this.onCancelFliClearanceConfirmationDialog();
    }

    onCancelFliClearanceConfirmationDialog(): void {
        this.showFliClearanceConfirmationDialog = false;
        this.fliSectionIndexToClear = undefined;
    }

    private addToEligibleFlis(fliToBeAdded: IFli) {
        for (let indexOfFormArray = 0; indexOfFormArray < this.changedData.flis.length; indexOfFormArray++) {
            const fliIndex = this.possibleSelectionOptions.findIndex((fli) => fli.name === fliToBeAdded.name);
            if (fliIndex != null) {
                let eligibleFlis = this.eligibleFlis.get(indexOfFormArray)
                    ? cloneDeep<IFli[]>(this.eligibleFlis.get(indexOfFormArray))
                    : [];
                eligibleFlis.splice(fliIndex, 0, fliToBeAdded);
                this.eligibleFlis.set(indexOfFormArray, eligibleFlis);
            } else {
                this.eligibleFlis.get(indexOfFormArray).push(fliToBeAdded);
            }
        }
    }

    // This function is to provide unique selection options that haven't been selected before
    getSelectionOptions(index: number): IFli[] {
        const selectedFlis: IFli[] = [];

        // Converting map values to an array
        for (const fli of this.selectedFlis.values()) {
            // fli might be undefined if it's not selected yet after clicking add more.
            if (fli) {
                selectedFlis.push(fli);
            }
        }

        this.prepareFliSelectionOptions(index, selectedFlis);
        const selectionOptions = this.eligibleFlis.get(index);

        return selectionOptions;
    }

    prepareFliSelectionOptions(index: number, selectedFlis: IFli[]) {
        const possibleFliOptions: IFli[] = [];
        const chosenFlis: IFli[] = selectedFlis;
        let selectedFliIndex: number | undefined;
        // Checking eligible options by comparing what is already selected with all flis
        this.flis.forEach((fli: IFli, index) => {
            const isSelected: IFli = chosenFlis.find((fliSelected) => fliSelected.id === fli.id);
            // If this fli wasn't already selected (isSelected is undefined), push it to the array of possibleFliOptions
            if (isSelected == null) {
                possibleFliOptions.push(fli);
            } else {
                selectedFliIndex = index;
            }
        });

        // Adding the already selected fli to the array of its own options
        const currentSelectedFli: IFli = this.selectedFlis.get(index);
        if (currentSelectedFli) {
            possibleFliOptions.splice(selectedFliIndex, 0, currentSelectedFli);
        }
        // REV-21347: the dropdown shows option['caption']; resolve any pre-migration object caption
        // to a report-language string on shallow copies so options never read "[object Object]"
        // (the source `this.flis` objects are left untouched).
        const normalizedOptions = possibleFliOptions.map((fli) => ({ ...fli, caption: this.resolveCaption(fli.caption) }));
        this.possibleSelectionOptions = cloneDeep<IFli[]>(normalizedOptions);
        this.eligibleFlis.set(index, normalizedOptions);
    }

    getMandatoryText(): string {
        return 'This field is mandatory.';
    }

    private addSvgIcon(name: string) {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    isFliSelectedToShowReference(formGroup: UntypedFormGroup) {
        const isFliSelected = formGroup['controls']['fli'].value !== '';
        const isFormGroupNotNull = formGroup['controls']['fli'].value != null;

        return isFormGroupNotNull && isFliSelected;
    }

    private resetFormValuesFromMapping(): void {
        if (this.fliData != null && this.flis != null) {
            this.fliData.flis = [...this.fliHelper.getUpdatedFlis(this.fliData.flis, this.flis)];
        }
        this.changedData = cloneDeep<IFinancialLineItemComponent>(this.fliData);
        this.addPreselectedFlis();
    }

    public drop(event: CdkDragDrop<any[]>): void {
        const { previousIndex, currentIndex } = event;

        moveItemInArray(this.form.get('fliSelectors')['controls'], previousIndex, currentIndex);
        moveItemInArray(this.form.get('fliSelectors')['value'], previousIndex, currentIndex);

        this.reorderSelectedFlis(previousIndex, currentIndex);

        const fliSelectors = this.form.value.fliSelectors;

        // Preserve footnotes mapping by id before reorder
        const prevFlis = cloneDeep(this.changedData.flis) || [];
        const prevFootnotes = this.changedData.footnotes ? [...this.changedData.footnotes] : [];
        const footnoteById = new Map<string, string>(prevFlis.map((fli, i) => [fli.id, prevFootnotes[i] ?? '']));

        this.changedData.flis = this.changedData.flis.sort((a, b) => {
            const aIndex = fliSelectors.findIndex((selector) => selector.fli.id === a.id);
            const bIndex = fliSelectors.findIndex((selector) => selector.fli.id === b.id);
            return aIndex - bIndex;
        });
        // Apply footnotes according to the new order
        if (footnoteById.size) {
            this.changedData.footnotes = this.changedData.flis.map((f) => footnoteById.get(f.id) ?? '');
        }
        // Also align UI input footnotes with the new order
        if (this.fliData) {
            if (!this.fliData.footnotes) {
                this.fliData.footnotes = [];
            }
            // Rebuild fliData.footnotes to mirror changedData.footnotes order
            this.fliData.footnotes = (this.changedData.footnotes || []).slice();
        }

        this.onDropFli.emit({ previousIndex, currentIndex });
        this.changeDetector.detectChanges();
    }

    private reorderSelectedFlis(previousIndex: number, currentIndex: number): void {
        const entries = Array.from(this.selectedFlis.entries());

        // Reorder entries
        const [movedEntry] = entries.splice(previousIndex, 1);
        entries.splice(currentIndex, 0, movedEntry);

        // Rebuild the selectedFlis map
        this.selectedFlis.clear();

        entries.forEach(([index, fli], newIndex) => {
            this.selectedFlis.set(newIndex, fli);
        });
    }

    setNumberingToFli(addNumbering: boolean, index: number): void {
        this.changedData.flis[index].addNumbering = addNumbering;
        this.updatePreselectedFlis();
    }

    private updatePreselectedFlis(): void {
        if (this.changedData.flis == null) {
            return;
        }
        let viewMode = this.state === ViewState.EDITING ? ViewMode.EDIT : ViewMode.PREVIEW;
        this.fliViewMode = [];
        if (this.changedData.flis.length) {
            this.changedData.flis.forEach((fli: IFli, sectionIndex: number) => {
                const formGroup = {};
                formGroup['fli'] = new UntypedFormControl(fli, Validators.required);
                this.fliFieldList[sectionIndex] = this.fb.group(formGroup);
                this.handleSelectionChange(fli, sectionIndex);
                this.fliViewMode?.push(viewMode);
            });
        } else {
            this.fliViewMode?.push(viewMode);
        }
    }

    getTableNumberIndex(fliSelectionIndex: number): number {
        return fliSelectionIndex > this.emptyFLIselector ? fliSelectionIndex - 1 : fliSelectionIndex;
    }

    get hasUnselectedFlis(): boolean {
        const controls = (this.form.get('fliSelectors') as UntypedFormArray).controls;
        return controls.some((control) => {
            const v = control.get('fli')?.value;
            return v == null || v === '';
        });
    }
}
